import Image from 'next/image';

import { Box, Heading, LinkOverlay, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { PromoCard } from '~/features/experiments/ab_plp_redesign_2025/components/promo-card';
import { CircleArrowRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { FlashSaleEyebrow } from '~/features/promotions/components/flash-sale-eyebrow';
import {
  type July4848PromoVariant,
  getJuly4848PromoCheckoutHref,
  JULY_48_48_PROMO_CTA_COLOR,
  JULY_48_48_PROMO_CTA_LABEL,
  JULY_48_48_PROMO_GRADIENT,
  JULY_48_48_PROMO_HEADLINE_COLOR,
  JULY_48_48_PROMO_SUBTITLE_COLOR,
  JULY_48_48_PROMO_VARIANTS,
} from '~/features/promotions/constants/july-48-48-promo';

/**
 * Homepage 48/48 promo card (Figma 1672:3880 desktop / 1672:3916 mobile): blue→purple gradient,
 * "FLASH SALE" eyebrow, two-line headline, subtitle, solid blue "Save Now" CTA. A thin wrapper
 * around the shared `PromoCard` with variant-specific background art.
 */
export function July4848PromoCard({
  variant,
  promoCode,
  imagePriority,
  onClick,
}: {
  variant: July4848PromoVariant;
  /** Live Braavos coupon from the promotion response; appended to the CTA as `?coupon=`. */
  promoCode?: string | null;
  imagePriority?: boolean;
  onClick?: () => void;
}) {
  const config = JULY_48_48_PROMO_VARIANTS[variant];
  const checkoutHref = getJuly4848PromoCheckoutHref(variant, promoCode);
  const cardId = `july-48-48-promo-${variant}-title`;

  const desktopBackground = (
    <Image
      alt=''
      loading='eager'
      priority={imagePriority}
      quality={90}
      sizes='(max-width: 768px) 100vw, 768px'
      src={config.homepageBg}
      style={{ objectFit: 'cover', objectPosition: 'right center' }}
      fill
    />
  );

  return (
    <PromoCard
      bgGradient={JULY_48_48_PROMO_GRADIENT}
      eyebrowSlot={<FlashSaleEyebrow />}
      id={cardId}
      backgroundImageSlot={
        <>
          <Box
            bottom={0}
            display={{ base: 'block', md: 'none' }}
            left={0}
            pointerEvents='none'
            position='absolute'
            right={0}
            top={0}
            zIndex={0}
          >
            <Image
              alt=''
              loading='eager'
              priority={imagePriority}
              quality={90}
              sizes='100vw'
              src={config.homepageBgMobile}
              style={{ objectFit: 'cover', objectPosition: 'right top' }}
              fill
            />
          </Box>
          <Box
            bottom={0}
            display={{ base: 'none', md: 'block' }}
            left={0}
            pointerEvents='none'
            position='absolute'
            right={0}
            top={0}
            zIndex={0}
          >
            {desktopBackground}
          </Box>
        </>
      }
      buttonSlot={
        <ButtonLink
          linkProps={{ href: checkoutHref }}
          buttonProps={{
            backgroundColor: JULY_48_48_PROMO_CTA_COLOR,
            color: 'white',
            colorScheme: 'blue',
            position: 'relative',
            rightIcon: <CircleArrowRightIcon boxSize='16px' color='white' aria-hidden />,
            size: 'md',
            variant: 'solid',
            zIndex: 1,
            width: {
              base: '100%',
              md: '152px',
            },
            onClick,
          }}
        >
          {JULY_48_48_PROMO_CTA_LABEL}
        </ButtonLink>
      }
      contentSlot={
        <Box>
          <Heading
            as='h2'
            color={JULY_48_48_PROMO_HEADLINE_COLOR}
            fontWeight='600'
            id={cardId}
            letterSpacing='-0.01em'
            mb={3}
            fontSize={{
              base: '1.75rem',
              md: '2.25rem',
            }}
            lineHeight={{
              base: '1.15',
              md: '1.1',
            }}
            whiteSpace={{
              base: 'pre-wrap',
              md: 'normal',
            }}
          >
            <LinkOverlay href={checkoutHref} onClick={onClick}>
              {`${config.headlineLine1}\n${config.headlineLine2}`}
            </LinkOverlay>
          </Heading>
          <Text color={JULY_48_48_PROMO_SUBTITLE_COLOR} fontSize='md' lineHeight={1.5}>
            {config.subtitle}
          </Text>
        </Box>
      }
      padding={{
        base: '32px',
        md: '48px 48px 56px 48px',
      }}
    />
  );
}
